import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
    Navbar,
    Nav,
    Container,
    NavDropdown,
    Button,
    Badge,
} from 'react-bootstrap';
import { useAuth } from '@/shared/contexts/AuthContext';
import {
    resolveRoles,
    activeRoleName,
    ROLE_NAMES,
} from '@/shared/utils/previewRoles';
import { isEmailVerified } from '@/shared/utils/emailVerified';
import { useFeature } from '@/shared/contexts/FeatureFlagContext';
import { leaveThen } from '@/shared/hooks/useLeaveGuard';
import { usePreviewTransitions } from '@/shared/hooks/usePreviewTransitions';
import { feedbackLinkFor } from '@/features/feedback/feedbackLink';
import notificationService from '@/shared/services/notificationService';
import './NavBar.css';

// The badge stops growing here; the accessible name keeps the real number.
const MAX_BADGE = 99;

/**
 * The unread count, refetched on every route change, when the window regains
 * focus, and when the notifications page reads something. The count lives in
 * the link's name and nowhere else, with no live region, so it is heard when
 * someone reaches the bell and is not announced on every page load.
 */
function NotificationBell() {
    const { pathname } = useLocation();
    const [unread, setUnread] = useState(0);
    // Only the newest request may set the count, so a slow response from an
    // earlier page cannot overwrite a fresher one.
    const latest = useRef(0);

    const refresh = useCallback(() => {
        latest.current += 1;
        const request = latest.current;
        notificationService
            .getUnreadCount()
            .then((data) => {
                if (request === latest.current) {
                    setUnread(data?.unreadCount || 0);
                }
            })
            .catch(() => {
                // Keep the last count; the next refetch corrects it.
            });
    }, []);

    useEffect(() => {
        refresh();
    }, [pathname, refresh]);

    useEffect(() => notificationService.subscribe(refresh), [refresh]);

    useEffect(() => {
        window.addEventListener('focus', refresh);
        return () => {
            window.removeEventListener('focus', refresh);
            // Discard whatever is still in flight
            latest.current += 1;
        };
    }, [refresh]);

    const label =
        unread > 0 ? `Notifications, ${unread} unread` : 'Notifications';

    return (
        <Nav.Link
            as={NavLink}
            to="/notifications"
            aria-label={label}
            className="psh-notification-bell position-relative"
        >
            <svg
                aria-hidden="true"
                focusable="false"
                width="20"
                height="20"
                viewBox="0 0 16 16"
                fill="currentColor"
            >
                <path d="M8 16a2 2 0 0 0 2-2H6a2 2 0 0 0 2 2m.995-14.901a1 1 0 1 0-1.99 0A5 5 0 0 0 3 6c0 1.098-.5 6-2 7h14c-1.5-1-2-5.902-2-7 0-2.42-1.72-4.44-4.005-4.901" />
            </svg>
            {unread > 0 && (
                <Badge
                    pill
                    bg="danger"
                    aria-hidden="true"
                    className="psh-notification-badge position-absolute"
                >
                    {unread > MAX_BADGE ? `${MAX_BADGE}+` : unread}
                </Badge>
            )}
        </Nav.Link>
    );
}

function NavBar() {
    const {
        currentUser,
        userProfile,
        effectiveRoles,
        previewRole,
        previewableRoles,
        logout,
        authMethods,
    } = useAuth();
    const roles = resolveRoles({ effectiveRoles, userProfile });
    const { enterPreview, exitPreview } = usePreviewTransitions();
    const navigate = useNavigate();
    const location = useLocation();
    // Where the person is, so the Feedback form can say which page the report
    // is about.
    const feedbackTo = feedbackLinkFor(location);
    // The Programs dropdown holds nothing but the three program dashboards,
    // which are behind coordinatorReview, so the dropdown goes with them
    // rather than rendering as an empty menu. Each entry keeps its own role
    // check when the flag is on.
    const coordinatorReviewEnabled = useFeature('coordinatorReview');
    // Surveys and training are off for launch (#391), each on its own flag.
    // Training has two entries, the student link and the user menu item, and
    // both go with it.
    const surveysEnabled = useFeature('surveys');
    const trainingEnabled = useFeature('training');
    // Open Positions reads the student profile and the position routes, which
    // sit behind these two flags.
    const studentApplicationEnabled = useFeature('studentApplication');
    const hiringEnabled = useFeature('hiring');

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/');
        } catch (_error) {
            navigate('/');
        }
    };

    const isVerified = isEmailVerified(currentUser);

    const isCoordinator = roles.some((role) =>
        [
            'si_coordinator',
            'ta_coordinator',
            'la_coordinator',
            'admin',
        ].includes(role)
    );

    const isStudent = roles.includes('student');

    // Requesting support is the thing faculty come here to do, so it is a
    // destination of its own rather than something reached only by finding a
    // course in the catalog first.
    const isFaculty = roles.some((role) => ['faculty', 'admin'].includes(role));

    return (
        <Navbar variant="dark" expand="xl" className="psh-navbar">
            <Container>
                <Navbar.Brand as={Link} to="/">
                    Peer Support Hub
                </Navbar.Brand>
                {/* Outside the collapsible menu so the count stays in view on
                    a phone, beside the menu button; last on a wide screen.
                    Needs a profile: the endpoints refuse an account without
                    one, and the page would send it back to create-profile. */}
                {currentUser && isVerified && userProfile && (
                    <Nav className="flex-row ms-auto me-2 ms-xl-2 me-xl-0 order-xl-last">
                        <NotificationBell />
                    </Nav>
                )}
                <Navbar.Toggle aria-controls="main-navbar" />
                <Navbar.Collapse id="main-navbar">
                    <Nav className="me-auto">
                        {!(currentUser && isVerified) && (
                            <>
                                <Nav.Link as={NavLink} to="/">
                                    Home
                                </Nav.Link>
                                <Nav.Link as={NavLink} to="/about">
                                    About
                                </Nav.Link>
                            </>
                        )}
                        {currentUser && isVerified && (
                            <Nav.Link as={NavLink} to="/dashboard">
                                Dashboard
                            </Nav.Link>
                        )}

                        {currentUser &&
                            isVerified &&
                            isStudent &&
                            studentApplicationEnabled &&
                            hiringEnabled && (
                                <Nav.Link as={NavLink} to="/positions">
                                    Positions
                                </Nav.Link>
                            )}

                        {currentUser && isVerified && isStudent && (
                            <>
                                {/* One page lists a dual-role person's
                                    applications and support requests, which
                                    the faculty link below already opens. */}
                                {!isFaculty && (
                                    <Nav.Link as={NavLink} to="/applications">
                                        Applications
                                    </Nav.Link>
                                )}
                                <Nav.Link as={NavLink} to="/assignments">
                                    Assignments
                                </Nav.Link>
                            </>
                        )}

                        {currentUser && isVerified && isFaculty && (
                            <Nav.Link as={NavLink} to="/applications">
                                Support requests
                            </Nav.Link>
                        )}

                        {currentUser &&
                            isVerified &&
                            isFaculty &&
                            hiringEnabled && (
                                <Nav.Link as={NavLink} to="/my-positions">
                                    Hiring
                                </Nav.Link>
                            )}

                        {currentUser && isVerified && !isStudent && (
                            <Nav.Link as={NavLink} to="/courses">
                                Courses
                            </Nav.Link>
                        )}

                        {currentUser &&
                            isVerified &&
                            isStudent &&
                            trainingEnabled && (
                                <Nav.Link as={NavLink} to="/training">
                                    Training
                                </Nav.Link>
                            )}

                        {currentUser && isVerified && surveysEnabled && (
                            <Nav.Link as={NavLink} to="/surveys">
                                Surveys
                            </Nav.Link>
                        )}
                        {currentUser && isVerified && isCoordinator && (
                            <>
                                <Nav.Link as={NavLink} to="/analytics">
                                    Analytics
                                </Nav.Link>
                                {coordinatorReviewEnabled && (
                                    <NavDropdown
                                        title="Programs"
                                        id="programs-dropdown"
                                    >
                                        {(roles.includes('si_coordinator') ||
                                            roles.includes('admin')) && (
                                            <NavDropdown.Item
                                                as={NavLink}
                                                to="/programs/si"
                                            >
                                                SI Program
                                            </NavDropdown.Item>
                                        )}
                                        {(roles.includes('ta_coordinator') ||
                                            roles.includes('admin')) && (
                                            <NavDropdown.Item
                                                as={NavLink}
                                                to="/programs/ta"
                                            >
                                                TA Program
                                            </NavDropdown.Item>
                                        )}
                                        {(roles.includes('la_coordinator') ||
                                            roles.includes('admin')) && (
                                            <NavDropdown.Item
                                                as={NavLink}
                                                to="/programs/la"
                                            >
                                                LA Program
                                            </NavDropdown.Item>
                                        )}
                                    </NavDropdown>
                                )}
                            </>
                        )}
                    </Nav>
                    <Nav>
                        {currentUser && isVerified ? (
                            <>
                                {/* A real link styled as a button: it
                                    navigates, so it must announce as a link */}
                                <Link
                                    to={feedbackTo}
                                    className="btn btn-outline-light btn-sm align-self-xl-center me-xl-2 my-2 my-xl-0"
                                >
                                    Feedback
                                </Link>
                                <NavDropdown
                                    title={
                                        <>
                                            {userProfile?.name ||
                                                currentUser.email}
                                            {activeRoleName(roles) && (
                                                <>
                                                    <span className="small fw-normal ms-1 d-none d-xxl-inline">
                                                        ({activeRoleName(roles)}
                                                        )
                                                    </span>
                                                    <span className="visually-hidden d-xxl-none">
                                                        ({activeRoleName(roles)}
                                                        )
                                                    </span>
                                                </>
                                            )}
                                        </>
                                    }
                                    id="user-dropdown"
                                    align="end"
                                >
                                    <NavDropdown.Item
                                        as={NavLink}
                                        to="/dashboard"
                                    >
                                        Dashboard
                                    </NavDropdown.Item>
                                    <NavDropdown.Item
                                        as={NavLink}
                                        to="/profile"
                                    >
                                        Profile
                                    </NavDropdown.Item>
                                    {!isStudent && (
                                        <NavDropdown.Item
                                            as={NavLink}
                                            to="/assignments"
                                        >
                                            My Assignments
                                        </NavDropdown.Item>
                                    )}
                                    {trainingEnabled && !isStudent && (
                                        <NavDropdown.Item
                                            as={NavLink}
                                            to="/training"
                                        >
                                            Training
                                        </NavDropdown.Item>
                                    )}
                                    <NavDropdown.Item
                                        as={NavLink}
                                        to="/dashboard"
                                        state={{ restartTour: true }}
                                    >
                                        Restart tour
                                    </NavDropdown.Item>
                                    {roles.includes('admin') && (
                                        <>
                                            <NavDropdown.Divider />
                                            <NavDropdown.Item
                                                as={NavLink}
                                                to="/admin"
                                            >
                                                Admin
                                            </NavDropdown.Item>
                                        </>
                                    )}
                                    {previewableRoles?.length > 0 && (
                                        <>
                                            <NavDropdown.Divider />
                                            <NavDropdown.Header>
                                                View as
                                            </NavDropdown.Header>
                                            {previewableRoles.map((role) => (
                                                <NavDropdown.Item
                                                    key={role}
                                                    as="button"
                                                    active={
                                                        previewRole === role
                                                    }
                                                    aria-label={`View the site as ${ROLE_NAMES[role]}`}
                                                    onClick={() =>
                                                        enterPreview(role)
                                                    }
                                                >
                                                    {ROLE_NAMES[role]}
                                                </NavDropdown.Item>
                                            ))}
                                            {previewRole && (
                                                <NavDropdown.Item
                                                    as="button"
                                                    onClick={exitPreview}
                                                >
                                                    My own view
                                                </NavDropdown.Item>
                                            )}
                                        </>
                                    )}
                                    <NavDropdown.Divider />
                                    {/* Not a link, so an open form's leave
                                    guard is asked first: it saves, or asks
                                    when it can't, before signing out */}
                                    <NavDropdown.Item
                                        onClick={() => leaveThen(handleLogout)}
                                    >
                                        Log Out
                                    </NavDropdown.Item>
                                </NavDropdown>
                            </>
                        ) : (
                            <>
                                <Nav.Link as={NavLink} to="/login">
                                    Log In
                                </Nav.Link>
                                {authMethods.includes('password') && (
                                    <Button
                                        as={Link}
                                        to="/signup"
                                        variant="outline-light"
                                        className="ms-2"
                                    >
                                        Sign Up
                                    </Button>
                                )}
                            </>
                        )}
                    </Nav>
                </Navbar.Collapse>
            </Container>
        </Navbar>
    );
}

export default NavBar;
